import { getAllOrganizations, IFilter } from "@/api";
import { IOrganizationMinimal } from "@/api/organizations/organizations.types";
import { TextField } from "@/components/shared/TextField";
import { ORGANIZATION_QUERY_FILTER } from "@/config/constants";
import { Autocomplete, AutocompleteInputChangeReason } from "@mui/material";
import { useDebouncedEffect } from "@react-hookz/web";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

const SEARCH_DEBOUNCE_DELAY = 250;
const SEARCH_DEBOUNCE_MAX_DELAY = 1000;
const ORGANIZATIONS_PAGE_SIZE = 100;

interface OrganizationAutocompleteBaseProps {
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
}

interface SingleProps extends OrganizationAutocompleteBaseProps {
    multiple?: false;
    value: number | null;
    onChange: (organizationId: number | null) => void;
    onChangeName?: (name: string | null) => void;
}

interface MultipleProps extends OrganizationAutocompleteBaseProps {
    multiple: true;
    value: IOrganizationMinimal[];
    onChange: (organizations: IOrganizationMinimal[]) => void;
    onChangeName?: never;
}

type OrganizationAutocompleteProps = SingleProps | MultipleProps;

export const OrganizationAutocomplete = (
    props: OrganizationAutocompleteProps
) => {
    const {
        multiple = false,
        label = "Organization",
        required = false,
        disabled = false,
        error = false,
        helperText,
    } = props;

    const [search, setSearch] = useState<string>("");
    const [queryFilter, setQueryFilter] = useState<IFilter>(
        ORGANIZATION_QUERY_FILTER
    );

    const { data, isPending } = useQuery({
        queryKey: ["organizations-autocomplete", queryFilter],
        queryFn: () =>
            getAllOrganizations(1, ORGANIZATIONS_PAGE_SIZE, queryFilter),
    });

    const organizationOptions = useMemo(() => data?.items ?? [], [data?.items]);

    useDebouncedEffect(
        () => {
            if (search === "") {
                setQueryFilter(ORGANIZATION_QUERY_FILTER);
                return;
            }

            setQueryFilter({
                ...ORGANIZATION_QUERY_FILTER,
                q: search,
            });
        },
        [search],
        SEARCH_DEBOUNCE_DELAY,
        SEARCH_DEBOUNCE_MAX_DELAY
    );

    const handleSearchChange = useCallback(
        (
            _: React.SyntheticEvent,
            newInputValue: string,
            reason: AutocompleteInputChangeReason
        ) => {
            if (reason === "input") {
                setSearch(newInputValue);
            }
        },
        []
    );

    if (multiple) {
        const { value, onChange } = props as MultipleProps;
        return (
            <Autocomplete
                multiple
                options={organizationOptions}
                value={value}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                onChange={(_, newValue) => {
                    onChange(newValue);
                }}
                onInputChange={handleSearchChange}
                disabled={disabled}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder="Select organizations"
                        required={required}
                        error={error}
                        helperText={helperText}
                        disableFocusShadow={false}
                    />
                )}
                filterOptions={(x) => x}
                size="small"
                autoComplete
                includeInputInList
                filterSelectedOptions
                loading={isPending}
                noOptionsText={
                    isPending ? "Loading..." : "No organizations found"
                }
                loadingText="Loading organizations..."
            />
        );
    }

    const { value, onChange, onChangeName } = props as SingleProps;
    const selectedOrganization =
        organizationOptions.find((org) => org.id === value) || null;

    return (
        <Autocomplete
            options={organizationOptions}
            value={selectedOrganization}
            getOptionLabel={(option) => option.name}
            onChange={(_, newValue) => {
                onChange(newValue?.id ?? null);
                onChangeName?.(newValue?.name ?? null);
            }}
            onInputChange={handleSearchChange}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder="Select an organization"
                    required={required}
                    error={error}
                    helperText={helperText}
                    disableFocusShadow={false}
                />
            )}
            filterOptions={(x) => x}
            size="small"
            autoComplete
            includeInputInList
            filterSelectedOptions
            loading={isPending}
            noOptionsText={isPending ? "Loading..." : "No organizations found"}
            loadingText="Loading organizations..."
        />
    );
};
